export const WAITING_ROOM_VERSION = 1;

export const WAITING_ROOM_EVENTS = {
    request: 'waiting-room/request',
    received: 'waiting-room/received',
    admit: 'waiting-room/admit',
    deny: 'waiting-room/deny',
    resolved: 'waiting-room/resolved',
};

const CONNECTED_CATEGORIES = new Set(['PNConnectedCategory', 'PNReconnectedCategory']);
const RESOLVED_REQUEST_TTL_MS = 60000;

export const isWaitingRoomEvent = (message, type) =>
    message &&
    message.version === WAITING_ROOM_VERSION &&
    message.type === type &&
    typeof message.requestId === 'string' &&
    typeof message.participantId === 'string';

export const normalizeWaitingRoomRequest = (message, publisher) => {
    if (!isWaitingRoomEvent(message, WAITING_ROOM_EVENTS.request)) return null;

    const participantId = message.participantId || publisher;
    if (!participantId) return null;

    return {
        requestId: message.requestId,
        participantId,
        displayName: String(message.displayName || 'Guest'),
        clientType: message.clientType || 'unknown',
        requestedAt: Number(message.requestedAt || message.sentAt) || Date.now(),
        receivedAt: Date.now(),
    };
};

export const requestFromPresence = (occupant) => {
    const state = occupant?.state;
    const participantId = occupant?.uuid;
    if (state?.role !== 'waiting' || !state.requestId || !participantId) return null;

    return {
        requestId: state.requestId,
        participantId,
        displayName: String(state.displayName || 'Guest'),
        clientType: state.clientType || 'unknown',
        requestedAt: Number(state.requestedAt) || Date.now(),
        receivedAt: Date.now(),
    };
};

export const requestFromLegacySignal = (message) => {
    const prefix = 'request_room-';
    if (typeof message !== 'string' || !message.startsWith(prefix)) return null;

    const payload = message.slice(prefix.length);
    const separatorIndex = payload.indexOf('_');
    const participantId = separatorIndex >= 0 ? payload.slice(0, separatorIndex) : payload;
    const displayName = separatorIndex >= 0 ? payload.slice(separatorIndex + 1) : 'Guest';
    if (!participantId) return null;

    return {
        requestId: `legacy-${participantId}`,
        participantId,
        displayName: displayName || 'Guest',
        clientType: 'legacy',
        requestedAt: Date.now(),
        receivedAt: Date.now(),
    };
};

export const upsertWaitingRoomRequest = (queue, request) => {
    if (!request) return queue;

    const existingIndex = queue.findIndex(
        (item) =>
            item.requestId === request.requestId || item.participantId === request.participantId,
    );

    if (existingIndex < 0) return [...queue, request];

    const existing = queue[existingIndex];
    if (existing.clientType !== 'legacy' && request.clientType === 'legacy') return queue;

    const updated = {
        ...existing,
        ...request,
        receivedAt: existing.receivedAt,
    };

    if (
        existing.requestId === updated.requestId &&
        existing.displayName === updated.displayName &&
        existing.clientType === updated.clientType
    ) {
        return queue;
    }

    const nextQueue = [...queue];
    nextQueue[existingIndex] = updated;
    return nextQueue;
};

export const removeWaitingRoomRequest = (queue, { requestId, participantId }) =>
    queue.filter(
        (request) =>
            request.requestId !== requestId && request.participantId !== participantId,
    );

const publishEvent = (client, room, message) =>
    client.publish({
        channel: room,
        message,
        storeInHistory: false,
    });

export const createModeratorWaitingRoomController = ({ client, room, onQueueChange }) => {
    let queue = [];
    const resolvedRequests = new Map();

    const emitQueue = (nextQueue) => {
        if (nextQueue === queue) return;
        queue = nextQueue;
        onQueueChange([...queue]);
    };

    const pruneResolvedRequests = () => {
        const cutoff = Date.now() - RESOLVED_REQUEST_TTL_MS;
        resolvedRequests.forEach((resolvedAt, key) => {
            if (resolvedAt < cutoff) resolvedRequests.delete(key);
        });
    };

    const isRecentlyResolved = (request) => {
        pruneResolvedRequests();
        return resolvedRequests.has(request.requestId);
    };

    const rememberResolved = (request) => {
        const resolvedAt = Date.now();
        resolvedRequests.set(request.requestId, resolvedAt);
    };

    const addRequest = (request) => {
        if (!request || isRecentlyResolved(request)) return;
        emitQueue(upsertWaitingRoomRequest(queue, request));
    };

    const removeRequest = (request) => {
        emitQueue(removeWaitingRoomRequest(queue, request));
    };

    const sendReceived = (request) =>
        publishEvent(client, room, {
            type: WAITING_ROOM_EVENTS.received,
            version: WAITING_ROOM_VERSION,
            requestId: request.requestId,
            participantId: request.participantId,
            sentAt: Date.now(),
        }).catch((error) => console.error('Unable to acknowledge waiting-room request', error));

    const reconcilePresence = async () => {
        try {
            const response = await client.hereNow({
                channels: [room],
                includeState: true,
            });
            const occupants = response.channels?.[room]?.occupants || [];
            const presenceRequests = occupants
                .map(requestFromPresence)
                .filter(Boolean)
                .sort((first, second) => first.requestedAt - second.requestedAt);
            const waitingParticipantIds = new Set(
                presenceRequests.map((request) => request.participantId),
            );
            const presentParticipantIds = new Set(
                occupants.map((occupant) => occupant.uuid).filter(Boolean),
            );
            let reconciledQueue = queue.filter(
                (request) =>
                    (request.clientType === 'legacy'
                        ? presentParticipantIds
                        : waitingParticipantIds
                    ).has(request.participantId),
            );
            presenceRequests.forEach((request) => {
                if (!isRecentlyResolved(request)) {
                    reconciledQueue = upsertWaitingRoomRequest(reconciledQueue, request);
                }
            });
            emitQueue(reconciledQueue);
        } catch (error) {
            console.error('Unable to reconcile waiting-room presence', error);
        }
    };

    const setModeratorPresence = () =>
        client
            .setState({
                channels: [room],
                state: { role: 'moderator' },
            })
            .catch((error) => console.error('Unable to set moderator presence', error));

    const listener = {
        message: ({ channel, message, publisher }) => {
            if (channel && channel !== room) return;

            const request = normalizeWaitingRoomRequest(message, publisher);
            if (request) {
                addRequest(request);
                sendReceived(request);
                return;
            }

            if (isWaitingRoomEvent(message, WAITING_ROOM_EVENTS.resolved)) {
                rememberResolved(message);
                removeRequest(message);
            }
        },
        signal: ({ channel, message }) => {
            if (channel && channel !== room) return;
            addRequest(requestFromLegacySignal(message));
        },
        presence: (event) => {
            if (event.channel && event.channel !== room) return;

            if (
                (event.action === 'join' || event.action === 'state-change') &&
                event.state?.role === 'waiting'
            ) {
                addRequest(
                    requestFromPresence({
                        uuid: event.uuid,
                        state: event.state,
                    }),
                );
            } else if (
                event.action === 'leave' ||
                event.action === 'timeout' ||
                (event.action === 'state-change' && event.state?.role !== 'waiting')
            ) {
                removeRequest({ participantId: event.uuid });
            }
        },
        status: ({ category }) => {
            if (!CONNECTED_CATEGORIES.has(category)) return;
            setModeratorPresence();
            reconcilePresence();
        },
    };

    const resolve = async (request, resolution) => {
        const type =
            resolution === 'admit' ? WAITING_ROOM_EVENTS.admit : WAITING_ROOM_EVENTS.deny;

        await publishEvent(client, room, {
            type,
            version: WAITING_ROOM_VERSION,
            requestId: request.requestId,
            participantId: request.participantId,
            sentAt: Date.now(),
        });

        const legacyMessage =
            resolution === 'admit'
                ? `join_room-${request.participantId}`
                : `deny_room-${request.participantId}`;
        client
            .signal({ channel: room, message: legacyMessage })
            .catch((error) => console.error('Unable to send legacy waiting-room response', error));

        rememberResolved(request);
        removeRequest(request);
    };

    return {
        connect() {
            client.addListener(listener);
            client.subscribe({ channels: [room], withPresence: true });
            setModeratorPresence();
            reconcilePresence();
        },
        disconnect() {
            client.removeListener(listener);
        },
        admit(request) {
            return resolve(request, 'admit');
        },
        deny(request) {
            return resolve(request, 'deny');
        },
        reconcile: reconcilePresence,
        getQueue() {
            return [...queue];
        },
    };
};
